import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { fetchSliderImages, addSliderImage, updateSliderImage, deleteSliderImage, fetchMainPages } from '../restaurantApi';
import config from '../config';

export default function RestaurantSlider() {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [openDialog, setOpenDialog] = useState(false);
  const [mainPages, setMainPages] = useState([]);
  
  // New Image State
  const [editingId, setEditingId] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newImageOrder, setNewImageOrder] = useState(0);
  const [newImageActive, setNewImageActive] = useState(true);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageSubtitle, setNewImageSubtitle] = useState('');
  const [newImageAction, setNewImageAction] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const [sliderData, pagesData] = await Promise.all([
        fetchSliderImages(),
        fetchMainPages()
      ]);
      setImages(sliderData);
      setMainPages(pagesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setNotification({ open: true, message: t('loadSliderImagesError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('deleteImageConfirmation'))) return;

    try {
      await deleteSliderImage(id);
      setNotification({ open: true, message: t('imageDeletedSuccess'), severity: 'success' });
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      setNotification({ open: true, message: t('deleteImageError'), severity: 'error' });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id || item.Id);
    setNewImageOrder(item.order || item.Order);
    setNewImageActive(item.isActive !== undefined ? item.isActive : item.IsActive);
    setNewImageTitle(item.title || item.Title || '');
    setNewImageSubtitle(item.subtitle || item.Subtitle || '');
    setNewImageAction(item.action || item.Action || '');
    
    const imagePath = item.imagePath || item.ImagePath;
    if (imagePath) {
      setNewImagePreview(imagePath.startsWith('http') ? imagePath : `${config.SITE_BASE_URL}/${imagePath.replace(/^\//, '')}`);
    } else {
      setNewImagePreview(null);
    }
    setNewImageFile(null);
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      setNewImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveImage = async () => {
    if (!editingId && !newImageFile) {
      setNotification({ open: true, message: t('selectImageWarning'), severity: 'warning' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      if (newImageFile) {
        formData.append('Image', newImageFile);
      }
      formData.append('Order', newImageOrder);
      formData.append('IsActive', newImageActive);
      formData.append('Title', newImageTitle);
      formData.append('Subtitle', newImageSubtitle);
      formData.append('Action', newImageAction);

      if (editingId) {
        await updateSliderImage(editingId, formData);
        setNotification({ open: true, message: t('imageUpdatedSuccess', 'Image updated successfully'), severity: 'success' });
      } else {
        await addSliderImage(formData);
        setNotification({ open: true, message: t('imageAddedSuccess'), severity: 'success' });
      }
      
      setOpenDialog(false);
      resetForm();
      fetchImages();
    } catch (error) {
      console.error('Error saving image:', error);
      setNotification({ open: true, message: editingId ? t('updateImageError', 'Error updating image') : t('addImageError'), severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setNewImageFile(null);
    setNewImagePreview(null);
    setNewImageOrder(0);
    setNewImageActive(true);
    setNewImageTitle('');
    setNewImageSubtitle('');
    setNewImageAction('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('sliderImages')}
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setOpenDialog(true)}
        >
          {t('addNewImage')}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('image')}</TableCell>
                  <TableCell>{t('title')}</TableCell>
                  <TableCell>{t('sortOrder')}</TableCell>
                  <TableCell>{t('status')}</TableCell>
                  <TableCell>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {images.map((item) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={item.id || item.Id || Math.random()}>
                    <TableCell>
                      <Box 
                        component="img" 
                        src={(item.imagePath || item.ImagePath || '').startsWith('http') 
                          ? (item.imagePath || item.ImagePath) 
                          : `${config.SITE_BASE_URL}/${(item.imagePath || item.ImagePath || '').replace(/^\//, '')}`} 
                        alt="Slider"
                        sx={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{item.title || item.Title}</Typography>
                      <Typography variant="caption" color="textSecondary">{item.subtitle || item.Subtitle}</Typography>
                    </TableCell>
                    <TableCell>{item.order || item.Order}</TableCell>
                    <TableCell>
                      <Chip 
                        label={(item.isActive !== undefined ? item.isActive : item.IsActive) ? t('active') : t('inactive')} 
                        color={(item.isActive !== undefined ? item.isActive : item.IsActive) ? "success" : "error"} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEdit(item)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDelete(item.id || item.Id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {images.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('noSliderImagesFound')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Image Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('editSliderImage', 'Edit Slider Image') : t('addNewSliderImage')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ textAlign: 'center', p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
              {newImagePreview ? (
                <Box 
                  component="img" 
                  src={newImagePreview} 
                  sx={{ maxWidth: '100%', maxHeight: 200, mb: 2, borderRadius: 1 }} 
                />
              ) : (
                <Typography color="textSecondary" sx={{ mb: 2 }}>{t('noImageSelected')}</Typography>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
              >
                {t('selectImage')}
                <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              </Button>
            </Box>

            <TextField
              label={t('title')}
              value={newImageTitle}
              onChange={(e) => setNewImageTitle(e.target.value)}
              fullWidth
            />
            
            <TextField
              label={t('subtitle')}
              value={newImageSubtitle}
              onChange={(e) => setNewImageSubtitle(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="action-select-label">{t('actionButtonText')}</InputLabel>
              <Select
                labelId="action-select-label"
                value={newImageAction}
                label={t('actionButtonText')}
                onChange={(e) => setNewImageAction(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('none')}</em>
                </MenuItem>
                {mainPages.map((page) => (
                  <MenuItem key={page.Id} value={page.PagePath}>
                    {t(`pages.${page.PageName}`) || page.PageName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('sortOrder')}
              type="number"
              value={newImageOrder}
              onChange={(e) => setNewImageOrder(e.target.value)}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={newImageActive}
                  onChange={(e) => setNewImageActive(e.target.checked)}
                  color="primary"
                />
              }
              label={t('active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleSaveImage} variant="contained" disabled={uploading}>
            {uploading ? (editingId ? t('updating', 'Updating...') : t('uploading')) : (editingId ? t('saveChanges') : t('addImage'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
